import { IconBeach, IconBuildingSkyscraper, IconBus, IconPlane } from "@tabler/icons-react";
import { useLoaderData, useNavigate } from "react-router";
import SaleBanners from "src/components/features/home/SaleBanners/SaleBanners";
import TopDestinations from "src/components/features/home/TopDestinations/TopDestinations";
import { Hero } from "src/components/features/home/Hero/Hero";
import { DesktopBenefitsStrip } from "src/components/features/home/DesktopBenefitsStrip/DesktopBenefitsStrip";
import { BusToursCarousel } from "src/components/features/home/BusTours/BusToursCarousel";
import { CharterDealsCarousel } from "src/components/features/home/CharterDeals/CharterDealsCarousel";
import { AvioCeljojumiCarousel } from "src/components/features/home/AvioCeljojumi/AvioCeljojumiCarousel";
import { SectionTransition } from "src/components/features/home/SectionTransition/SectionTransition";
import { charterDeals } from "src/components/features/home/CharterDeals/charterDeals";
import { ProductCarouselSection } from "src/components/features/home/ProductCarouselSection/ProductCarouselSection";
import { VacationDealCard } from "src/components/features/home/ProductCarouselSection/VacationDealCard";
import { BusTourDealCard } from "src/components/features/home/ProductCarouselSection/BusTourDealCard";
import { FlightHotelDealCard } from "src/components/features/home/ProductCarouselSection/FlightHotelDealCard";
import { doMoneyMultiplication } from "src/utils/money-utils";
import { IMG_SIZES } from "src/pages/loaders/UserTripsLoader";
import { useDeviceContext } from "src/context/device-context";
import { useLocaleContext } from "src/context/locale-context";
import useLoginPopup from "src/hooks/useLoginPopup";
import { FlightBookingGuide } from "../components/features/home/FlightBookingGuide/FlightBookingGuide";
import { CheapDestinations } from "../components/features/home/CheapDestinations/CheapDestinations";
import FlightSearchForm from "src/components/features/search-form/SearchForm/FlightSearchForm";

const TEMP_ALLOWED_COMPANIES = ["celotleti"];

function HomePage() {
  const { isMobile } = useDeviceContext();
  const { getStringRes } = useLocaleContext();
  const navigate = useNavigate();
  const sectionText = (key) => getStringRes(key);
  const {
    bookingGuide,
    topDestinations,
    saleBanners,
    cheapDestinations,
    cheapHotels,
    cheapTours,
    // Renamed to avoid shadowing the `charterDeals` mock import above (still used by the mobile
    // "vacation" ProductCarouselSection below) — this is the real admin-managed list, used only
    // by the desktop CharterDealsCarousel.
    charterDeals: charterDealsLoader,
  } = useLoaderData();
  useLoginPopup();

  // Real admin-managed deals when there are any, otherwise the hardcoded placeholder list —
  // same fallback the desktop CharterDealsCarousel already uses, now shared by mobile's
  // "vacation" ProductCarouselSection too (it used to always show the hardcoded mock only).
  const charterDealsData = charterDealsLoader.data?.length ? charterDealsLoader.data : charterDeals;

  // Same idea for mobile's "flightHotel" section — real cheapHotels data (same source as
  // desktop's Avio ceļojumi), mapped down to the same shape AvioHotelCard uses (price =
  // per-person, computed the same way AvioHotelCard/CompactHotelCard already do) so the mobile
  // card can show the same info blocks (package label, hotel+rating, dates) as desktop, not a
  // stripped-down subset. image carries the "{size}" template from the API — AvioHotelCard
  // resolves it the same way; skipping that step left mobile showing the broken-image fallback
  // icon instead of the real photo.
  const flightHotelDealsData = (cheapHotels.data || []).map((item) => {
    const guestCount = Array.isArray(item.guests)
      ? item.guests.reduce((sum, count) => sum + Number(count || 0), 0)
      : parseInt(item.guests) || 1;
    const pricePerPerson =
      item.totalPrice && guestCount
        ? doMoneyMultiplication(parseFloat(item.totalPrice), 1 / guestCount)
        : null;
    return {
      id: item.id,
      city: item.city || item.name,
      name: item.name,
      rating: item.rating,
      dates: item.dates,
      discount: item.discount,
      price: pricePerPerson,
      image: item.image?.replace("{size}", IMG_SIZES.medium),
      link: item.link,
    };
  });

  return (
    <main id="tickets-home-page" className={isMobile ? "mobile" : "desktop"}>
      <div className="home-content white">
        <Hero />
        <FlightSearchForm />
        {!isMobile && <DesktopBenefitsStrip />}
        {/* Temporarily hidden per request — keep SaleBanners wired, just don't render it. */}
        {false && <SaleBanners saleData={saleBanners.data} />}

        {isMobile && (
          <>
            <ProductCarouselSection
              variant="vacation"
              icon={<IconBeach size={26} stroke={1.75} color="#c88a3a" />}
              title={sectionText("home.section.vacation.title")}
              subtitle={sectionText("home.section.vacation.subtitle")}
              viewAllLabel={sectionText("home.section.viewAll")}
              onViewAll={() => navigate("/charter-flights")}
              items={charterDealsData}
              maxItems={8}
              renderItem={(deal) => <VacationDealCard deal={deal} />}
            />
            <ProductCarouselSection
              variant="flightHotel"
              icon={
                <>
                  <IconPlane size={22} stroke={1.75} color="#6b4fbb" />
                  <IconBuildingSkyscraper size={22} stroke={1.75} color="#6b4fbb" />
                </>
              }
              title={sectionText("home.section.flightHotel.title")}
              subtitle={sectionText("home.section.flightHotel.subtitle")}
              viewAllLabel={sectionText("home.section.viewAll")}
              items={flightHotelDealsData}
              maxItems={10}
              renderItem={(deal) => <FlightHotelDealCard deal={deal} />}
            />
            <ProductCarouselSection
              variant="bus"
              icon={<IconBus size={26} stroke={1.75} color="#2f6fd1" />}
              title={sectionText("home.section.bus.title")}
              subtitle={sectionText("home.section.bus.subtitle")}
              viewAllLabel={sectionText("home.section.viewAll")}
              onViewAll={() => navigate("/bus-tours")}
              items={cheapTours.data}
              maxItems={6}
              renderItem={(tour) => <BusTourDealCard tour={tour} />}
            />
          </>
        )}

        {/* Desktop product-section order (Atpūtas → Avio → Autobusu) reads as "complete package
            → self-built package → excursion trip". On mobile, the three ProductCarouselSection
            blocks above already cover the same deals, so these desktop-only carousels are
            skipped there to avoid duplicating content. "Lēti viesnīcu piedāvājumi" (CheapHotels
            Carousel) was removed from both desktop and mobile per explicit request — cheapHotels
            data is still loaded/used though, it's the same source Avio ceļojumi pulls from. */}
        {!isMobile && (
          <>
            <CharterDealsCarousel data={charterDealsLoader.data} />
            <SectionTransition />
            <AvioCeljojumiCarousel data={cheapHotels.data} />
            <BusToursCarousel data={cheapTours.data} />
          </>
        )}
        <TopDestinations travelDeals={topDestinations.data} />
      </div>
      <div className="home-content off-white">
        <CheapDestinations data={cheapDestinations.data} />
        {!bookingGuide.error &&
          TEMP_ALLOWED_COMPANIES.includes(window.SERVER_DATA.REACT_APP_TENANT_NAME) && (
            <FlightBookingGuide data={bookingGuide.data} />
          )}
      </div>
    </main>
  );
}

export default HomePage;
